import type {
  ContainerNode,
  LayoutDocument,
  LayoutListComponentProps,
  LayoutNode,
} from "../layout/types.js";
import type {
  CompileNodeContext,
  CompiledLayoutDocument,
  CompiledLayoutRuntimeState,
  CompiledNode,
  RenderNode,
  RuntimeEffect,
  RuntimeFlowContinuation,
  RuntimeContext,
} from "./types.js";
import { compileNode } from "./compileNode.js";
import { uniqueDependencies } from "./method.js";
import { compileSceneCatalog } from "./compileSceneCatalog.js";
import { compileListFilterTransitionBuckets } from "./compileListFilterTransitionBuckets.js";
import { scaleRootRenderNodeZIndex } from "./renderClip.js";
import { buildRuntimeVariableVersionSignature } from "./runtimeVariables.js";

const MAX_RUNTIME_VARIABLE_EVALUATION_PASSES = 5;

type CompileDocumentOptions = {
  runtimeState?: CompiledLayoutRuntimeState;
  /** Immutable runtime documents can share bytecode, never execution state. */
  reuseProgram?: boolean;
};

function dedupeContinuations(
  continuations: RuntimeFlowContinuation[],
): RuntimeFlowContinuation[] {
  return [...new Map(continuations.map((item) => [item.key, item])).values()];
}

/**
 * Re-emits every pending async continuation stored in runtime state.
 *
 * Needed when a parent absolute/flex node cache-hits and skips child `evaluate`
 * calls: child waits were persisted but never pushed into this pass's
 * `__runtimeContinuations`, so the renderer would otherwise drop them
 * (especially under React Strict Mode double-evaluate).
 */
function collectPersistedContinuations(
  runtimeState: CompiledLayoutRuntimeState,
): RuntimeFlowContinuation[] {
  const collected: RuntimeFlowContinuation[] = [];
  for (const pending of runtimeState.runtimeContinuationStateByNodeKey.values()) {
    collected.push(...pending);
  }
  return collected;
}

export function createCompiledLayoutRuntimeState(): CompiledLayoutRuntimeState {
  return {
    flowStateByNodeKey: new Map(),
    renderCacheByNodeKey: new Map(),
    runtimePatchStateByNodeKey: new Map(),
    runtimeContinuationStateByNodeKey: new Map(),
    runtimeVariableStateById: new Map<string, unknown>(),
    runtimeVariableVersionById: new Map<string, number>(),
    customEventTriggerStateByKey: new Map(),
  };
}

export function resetCompiledLayoutRuntimeState(
  state: CompiledLayoutRuntimeState,
): void {
  state.flowStateByNodeKey.clear();
  state.renderCacheByNodeKey.clear();
  state.runtimePatchStateByNodeKey.clear();
  state.runtimeContinuationStateByNodeKey.clear();
  state.runtimeVariableStateById.clear();
  state.runtimeVariableVersionById.clear();
  state.customEventTriggerStateByKey.clear();
}

function keyBelongsToNode(key: string, nodeId: string): boolean {
  return (
    key === nodeId ||
    key.startsWith(`${nodeId}:`) ||
    key.endsWith(`:${nodeId}`) ||
    key.includes(`:${nodeId}:`)
  );
}

function deleteKeysForNodeIds<T>(map: Map<string, T>, nodeIds: Set<string>) {
  for (const key of map.keys()) {
    for (const nodeId of nodeIds) {
      if (keyBelongsToNode(key, nodeId)) {
        map.delete(key);
        break;
      }
    }
  }
}

function collectAncestorIdsForNodes(
  nodes: LayoutNode[],
  targetNodeIds: Set<string>,
): Set<string> {
  const ancestors = new Set<string>();

  const visit = (node: LayoutNode, path: string[]): boolean => {
    let containsTarget = targetNodeIds.has(node.id);

    const layoutListProps =
      node.kind === "component" && node.component === "layoutList"
        ? ((node.props ?? {}) as Partial<LayoutListComponentProps>)
        : undefined;
    const rowTemplate = layoutListProps?.rowTemplate;
    if (rowTemplate && visit(rowTemplate, [...path, node.id])) {
      containsTarget = true;
    }
    for (const variant of layoutListProps?.rowVariants ?? []) {
      if (visit(variant.template as ContainerNode, [...path, node.id])) {
        containsTarget = true;
      }
    }

    if (node.kind === "container") {
      for (const child of node.children) {
        if (visit(child, [...path, node.id])) {
          containsTarget = true;
        }
      }
    }

    if (containsTarget) {
      for (const ancestorId of path) {
        ancestors.add(ancestorId);
      }
    }

    return containsTarget;
  };

  for (const node of nodes) {
    visit(node, []);
  }

  return ancestors;
}

export function invalidateCompiledLayoutRuntimeState(
  state: CompiledLayoutRuntimeState,
  document: LayoutDocument,
  nodeIds: readonly string[],
): void {
  const behaviorNodeIds = new Set(nodeIds.filter(Boolean));
  if (behaviorNodeIds.size === 0) {
    return;
  }

  const renderNodeIds = new Set([
    ...behaviorNodeIds,
    ...collectAncestorIdsForNodes(document.scene.children, behaviorNodeIds),
  ]);

  deleteKeysForNodeIds(state.renderCacheByNodeKey, renderNodeIds);
  deleteKeysForNodeIds(state.flowStateByNodeKey, behaviorNodeIds);
  deleteKeysForNodeIds(state.runtimePatchStateByNodeKey, behaviorNodeIds);
  deleteKeysForNodeIds(
    state.runtimeContinuationStateByNodeKey,
    behaviorNodeIds,
  );
  deleteKeysForNodeIds(state.customEventTriggerStateByKey, behaviorNodeIds);
}

function compileProgram(document: LayoutDocument) {
  const compiledSceneCatalog = compileSceneCatalog(document.scene.catalog);
  const listFilterTransitions = compileListFilterTransitionBuckets(
    document,
    compiledSceneCatalog.dependencyIndex,
  );

  const compileContext: CompileNodeContext = {
    catalogDeps: compiledSceneCatalog.dependencyIndex,
    layoutNodeById: listFilterTransitions.layoutNodeById,
    listFilterTransitionBucketsByListId: listFilterTransitions.bucketsByListId,
    customEventTriggerDependenciesById: Object.fromEntries(
      Object.entries(compiledSceneCatalog.customEventTriggers).map(
        ([id, trigger]) => [id, trigger.dependencies],
      ),
    ),
  };

  const compiledRootNodes: CompiledNode[] = document.scene.children
    .map((node) => compileNode(node, compileContext))
    .filter((node): node is CompiledNode => node !== null);

  return { compiledSceneCatalog, compiledRootNodes };
}

const programs = new WeakMap<
  LayoutDocument,
  ReturnType<typeof compileProgram>
>();
const shareableDocuments = new WeakMap<LayoutDocument, boolean>();

function canShareProgram(document: LayoutDocument): boolean {
  const known = shareableDocuments.get(document);
  if (known !== undefined) return known;
  // Layout lists currently own row lifecycle/animation state in their compiled
  // closure. Keep those programs private, including lists inside tooltips.
  const visited = new WeakSet<object>();
  const containsList = (value: unknown): boolean => {
    if (!value || typeof value !== "object" || visited.has(value)) return false;
    visited.add(value);
    if ((value as { component?: string }).component === "layoutList")
      return true;
    return Object.values(value).some(containsList);
  };
  const shareable = !containsList(document);
  shareableDocuments.set(document, shareable);
  return shareable;
}

export function compileDocument(
  document: LayoutDocument,
  options: CompileDocumentOptions = {},
): CompiledLayoutDocument {
  const share = options.reuseProgram && canShareProgram(document);
  let program = share ? programs.get(document) : undefined;
  if (!program) {
    program = compileProgram(document);
    if (share) programs.set(document, program);
  }
  const { compiledSceneCatalog, compiledRootNodes } = program;

  const runtimeState =
    options.runtimeState ?? createCompiledLayoutRuntimeState();

  return {
    dependencies: uniqueDependencies([
      ...compiledSceneCatalog.dependencies,
      ...compiledRootNodes.flatMap((node) => node.dependencies),
    ]),

    evaluate(data: RuntimeContext) {
      const effects: RuntimeEffect[] = [];
      let continuations: RuntimeFlowContinuation[] = [];
      let nodes: RenderNode[] = [];
      let previousVariableVersionSignature =
        buildRuntimeVariableVersionSignature(
          runtimeState.runtimeVariableVersionById,
        );

      for (
        let passIndex = 0;
        passIndex < MAX_RUNTIME_VARIABLE_EVALUATION_PASSES;
        passIndex += 1
      ) {
        const runtimeEffects: RuntimeEffect[] = [];
        const runtimeContinuations: RuntimeFlowContinuation[] = [];

        const runtimeContext: RuntimeContext = {
          ...data,
          __sceneVariableCache: new Map(),
          __signatureValueCache: data.__cacheSourceSignatures
            ? (data.__signatureValueCache ?? new WeakMap())
            : undefined,
          __expressionCache: data.__cacheSourceSignatures
            ? { byContext: new WeakMap() }
            : undefined,
          __variables: compiledSceneCatalog.variables,
          __functions: compiledSceneCatalog.functions,
          __animations: compiledSceneCatalog.animations,
          __animationsByName: compiledSceneCatalog.animationsByName,
          __customEventTriggers: compiledSceneCatalog.customEventTriggers,
          __customEventTriggersByName:
            compiledSceneCatalog.customEventTriggersByName,
          __customEventTriggerStateByKey:
            runtimeState.customEventTriggerStateByKey,
          __flowStateByNodeKey: runtimeState.flowStateByNodeKey,
          __renderCacheByNodeKey: runtimeState.renderCacheByNodeKey,
          __runtimePatchStateByNodeKey: runtimeState.runtimePatchStateByNodeKey,
          __runtimeContinuationStateByNodeKey:
            runtimeState.runtimeContinuationStateByNodeKey,
          __runtimeVariableStateById: runtimeState.runtimeVariableStateById,
          __runtimeVariableVersionById: runtimeState.runtimeVariableVersionById,
          __runtimeEffects: runtimeEffects,
          __runtimeContinuations: runtimeContinuations,
          __resumeContinuations:
            passIndex === 0 ? data.__resumeContinuations : [],
          __controlRoomDispatches:
            passIndex === 0 ? data.__controlRoomDispatches : [],
          __listTransitionDispatches:
            passIndex === 0 ? data.__listTransitionDispatches : [],
          __customDisciplineClickDispatches:
            passIndex === 0 ? data.__customDisciplineClickDispatches : [],
        };

        const passNodes: RenderNode[] = [];

        for (const compiledNode of compiledRootNodes) {
          const rendered = compiledNode.evaluate(runtimeContext);
          if (rendered) {
            passNodes.push(scaleRootRenderNodeZIndex(rendered));
          }
        }

        nodes = passNodes;
        effects.push(...runtimeEffects);
        continuations = dedupeContinuations([
          ...continuations,
          ...runtimeContinuations,
        ]);

        const nextVariableVersionSignature =
          buildRuntimeVariableVersionSignature(
            runtimeState.runtimeVariableVersionById,
          );
        if (nextVariableVersionSignature === previousVariableVersionSignature) {
          break;
        }
        previousVariableVersionSignature = nextVariableVersionSignature;
      }

      // Always merge persisted waits so parent cache-hits cannot drop them.
      continuations = dedupeContinuations([
        ...continuations,
        ...collectPersistedContinuations(runtimeState),
      ]);

      return {
        canvas: document.meta.canvas,
        nodes,
        effects,
        continuations,
      };
    },
  };
}
